import { Box, Button, Collapse, Grow, SxProps, Theme, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  COUNTRY_HINT_ARROW_MIN_RIGHT,
  COUNTRY_HINT_ARROW_SIZE,
  COUNTRY_HINT_CHANGE_LABEL,
  COUNTRY_HINT_DELAY_MS,
  COUNTRY_HINT_GOT_IT_LABEL,
  COUNTRY_HINT_NON_US_BODY,
  COUNTRY_HINT_OFFSET,
  COUNTRY_HINT_TRANSITION,
  COUNTRY_HINT_UNKNOWN_BODY,
  COUNTRY_HINT_UNKNOWN_TITLE,
  COUNTRY_HINT_US_BODY,
  COUNTRY_HINT_US_TITLE,
  COUNTRY_HINT_VIEWPORT_GUTTER,
  COUNTRY_HINT_WIDTH,
  getCountryHintTitle,
  SELECT_COUNTRY_LABEL,
} from '../../../constants/country';
import { COUNTRY_HINT_Z_INDEX } from '../../../constants/ui';
import { selectHasRecipientSelection } from '../../../store/selectors/derived';
import {
  selectCountryReason,
  selectEffectiveCountryName,
  selectIsEffectiveCountryUs,
  selectUserCountry,
} from '../../../store/selectors/init';
import { selectInitKeys } from '../../../store/selectors/keys';
import { CountryReasonEnum } from '../../../types/country';
import { readHintDismissed, storeHintDismissed } from '../../../util/country';

interface IProps {
  wrapperRef: React.RefObject<HTMLElement>;
  anchorRef: React.RefObject<HTMLElement>;
  onChange: () => void;
  isInline?: boolean;
}

const DEFAULT_ARROW_RIGHT = 24;

const liveRegionSx: SxProps<Theme> = {
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  pointerEvents: 'none',
};

const calloutSx: SxProps<Theme> = {
  position: 'absolute',
  zIndex: COUNTRY_HINT_Z_INDEX,
  pointerEvents: 'auto',
  top: `calc(100% + ${COUNTRY_HINT_OFFSET}px)`,
  right: 0,
  width: `min(${COUNTRY_HINT_WIDTH}px, 100vw - ${COUNTRY_HINT_VIEWPORT_GUTTER}px)`,
  p: 2,
  boxSizing: 'border-box',
  backgroundColor: 'background.paper',
  borderRadius: 1,
  boxShadow: 8,
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
  textAlign: 'left',
};

const arrowSx: SxProps<Theme> = {
  position: 'absolute',
  top: -COUNTRY_HINT_ARROW_SIZE / 2,
  width: COUNTRY_HINT_ARROW_SIZE,
  height: COUNTRY_HINT_ARROW_SIZE,
  backgroundColor: 'background.paper',
  transform: 'rotate(45deg)',
  boxShadow: ({ palette }) => `-2px -2px 3px ${alpha(palette.common.black, 0.06)}`,
};

const textSx: SxProps<Theme> = { display: 'flex', flexDirection: 'column', gap: 0.5 };

const lineSx: SxProps<Theme> = { fontSize: 14, lineHeight: '20px', textWrap: 'pretty' };

const actionsSx: SxProps<Theme> = { display: 'flex', justifyContent: 'flex-end', gap: 1 };

const actionButtonSx: SxProps<Theme> = { height: 36, fontSize: 14 };

const inlinePanelSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  pt: 1.5,
  px: 2,
  pb: 1,
  borderTop: 1,
  borderColor: 'divider',
  backgroundColor: 'grey.50',
  overflow: 'hidden',
  boxSizing: 'border-box',
  textAlign: 'left',
};

const inlineTextSx: SxProps<Theme> = { display: 'flex', flexDirection: 'column', gap: 0.25 };

const inlineActionsSx: SxProps<Theme> = { display: 'flex', justifyContent: 'flex-end', gap: 1, pb: 0.5 };

export const CountryHint: React.FC<IProps> = ({ wrapperRef, anchorRef, onChange, isInline = false }) => {
  const keys = useSelector(selectInitKeys);
  const userCountry = useSelector(selectUserCountry);
  const isUs = useSelector(selectIsEffectiveCountryUs);
  const reason = useSelector(selectCountryReason);
  const countryName = useSelector(selectEffectiveCountryName);
  const hasSelection = useSelector(selectHasRecipientSelection);

  const titleId = useId();
  const [isEligible] = useState(() => !userCountry && !readHintDismissed(keys));
  const [isDismissed, setIsDismissed] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [arrowRight, setArrowRight] = useState(DEFAULT_ARROW_RIGHT);

  const isActive = isEligible && !isDismissed;

  const dismiss = useCallback(() => {
    setIsDismissed(true);
    setIsOpen(false);
    storeHintDismissed(keys);
  }, [keys]);

  useEffect(() => {
    if (!isActive) {
      return undefined;
    }

    const timer = window.setTimeout(() => setIsOpen(true), COUNTRY_HINT_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [isActive]);

  useEffect(() => {
    if (isActive && (userCountry || hasSelection)) {
      dismiss();
    }
  }, [isActive, userCountry, hasSelection, dismiss]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        dismiss();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, dismiss]);

  useLayoutEffect(() => {
    if (!isOpen || isInline) {
      return;
    }

    const wrapperRect = wrapperRef.current?.getBoundingClientRect();
    const anchorRect = anchorRef.current?.getBoundingClientRect();

    if (wrapperRect && anchorRect && anchorRect.width) {
      const anchorCenter = anchorRect.left + anchorRect.width / 2;

      setArrowRight(
        Math.max(COUNTRY_HINT_ARROW_MIN_RIGHT, wrapperRect.right - anchorCenter - COUNTRY_HINT_ARROW_SIZE / 2),
      );
    }
  }, [isOpen, isInline, wrapperRef, anchorRef]);

  const handleChange = useCallback(() => {
    dismiss();
    onChange();
  }, [dismiss, onChange]);

  const copy = useMemo(() => {
    if (isUs) {
      return { title: COUNTRY_HINT_US_TITLE, body: COUNTRY_HINT_US_BODY, action: COUNTRY_HINT_CHANGE_LABEL };
    }

    if (reason === CountryReasonEnum.Unknown || !countryName) {
      return { title: COUNTRY_HINT_UNKNOWN_TITLE, body: COUNTRY_HINT_UNKNOWN_BODY, action: SELECT_COUNTRY_LABEL };
    }

    return {
      title: getCountryHintTitle(countryName),
      body: COUNTRY_HINT_NON_US_BODY,
      action: COUNTRY_HINT_CHANGE_LABEL,
    };
  }, [isUs, reason, countryName]);

  if (!isEligible) {
    return null;
  }

  const content = (
    <>
      <Box sx={isInline ? inlineTextSx : textSx}>
        <Typography id={titleId} component="span" color="text.primary" sx={lineSx}>
          {copy.title}
        </Typography>
        <Typography component="span" color="text.secondary" sx={lineSx}>
          {copy.body}
        </Typography>
      </Box>
      <Box sx={isInline ? inlineActionsSx : actionsSx}>
        <Button variant="text" color="primary" size="small" onClick={handleChange} sx={actionButtonSx}>
          {copy.action}
        </Button>
        <Button variant="outlined" color="primary" size="small" onClick={dismiss} sx={actionButtonSx}>
          {COUNTRY_HINT_GOT_IT_LABEL}
        </Button>
      </Box>
    </>
  );

  if (isInline) {
    return (
      <Box role="status">
        <Collapse in={isOpen} timeout={COUNTRY_HINT_TRANSITION} unmountOnExit>
          <Box role="group" aria-labelledby={titleId} sx={inlinePanelSx}>
            {content}
          </Box>
        </Collapse>
      </Box>
    );
  }

  return (
    <Box role="status" sx={liveRegionSx}>
      <Grow in={isOpen} timeout={COUNTRY_HINT_TRANSITION} unmountOnExit>
        <Box
          role="group"
          aria-labelledby={titleId}
          sx={calloutSx}
          style={{ transformOrigin: `calc(100% - ${arrowRight + COUNTRY_HINT_ARROW_SIZE / 2}px) top` }}
        >
          <Box aria-hidden sx={{ ...arrowSx, right: arrowRight }} />
          {content}
        </Box>
      </Grow>
    </Box>
  );
};
